import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, cn } from '@c2fo/liquidity';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import MarketingWidgetTemplate, {
  MarketingWidgetTemplateProps,
  MarketingWidgetTrackingMetadata,
} from './MarketingWidgetTemplate';

/** Cap on cards in the homepage deck. */
export const MARKETING_WIDGET_DECK_LIMIT = 3;

/** How far each card behind the front one shows above it, and how much smaller it is. */
const PEEK_OFFSET_PX = 12;
const PEEK_SCALE_STEP = 0.05;

/**
 * How much page background each card behind the front one is washed with, so it recedes into fog.
 *
 * Reason: this is a scrim painted *over* each back card, not opacity on the card itself. Fading a
 * card would show the card behind it through the partly transparent gradient; a scrim keeps every
 * card opaque over its own `bg-white` and only ever reveals that same card beneath the fog.
 */
const PEEK_FOG_STEP = 0.3;

const getWidgetKey = (widget: MarketingWidgetTemplateProps, index: number): string =>
  widget.trackingMetadata.artifactUuid ?? widget.trackingMetadata.widgetType ?? `widget-${index}`;

/** `widget::carousel::shown` — what the supplier could reach, once per deck composition. */
interface CarouselReport {
  location: 'homepage';
  widgetCount: number;
  /**
   * One entry per widget, in deck order, carrying that widget's own tracking metadata so an entry
   * joins to its `widget::shown` on fields the warehouse already models. Every entry has a slot, which
   * the metadata type leaves optional for the single-card paths.
   */
  widgets: (MarketingWidgetTrackingMetadata & { position: number })[];
}

interface MarketingWidgetDeckProps {
  widgets: MarketingWidgetTemplateProps[];
}

/**
 * Stacked homepage marketing widgets with previous/next text buttons.
 * One widget renders the plain card. Cards behind the front one are inert and out of the tab order.
 * All cards share one grid cell, so the stack is as tall as the tallest card and advancing does not resize the column.
 */
const MarketingWidgetDeck = ({ widgets }: MarketingWidgetDeckProps) => {
  const { t } = useTranslation();
  const { addAction } = useDataDog();
  const { trackEnterpriseEvent } = useReporting();
  const [activeIndex, setActiveIndex] = useState(0);
  const deck = widgets.slice(0, MARKETING_WIDGET_DECK_LIMIT);
  const count = deck.length;

  /**
   * The deck's contents in order. `widgets` is a fresh array on every render, so this — not the array
   * identity — is what decides whether the supplier is looking at a new carousel.
   */
  const compositionKey = deck.map(getWidgetKey).join('>');

  /**
   * Compared against `compositionKey` rather than against `deck`, so re-resolving the same widgets does
   * not look like a new carousel while a supplier whose eligible widgets change mid-session gets a
   * second report with the new slots. Mount-scoped: leaving the homepage and returning reports again,
   * which is intended — the event counts presentations of a deck, not distinct sets of widgets.
   */
  const reportedCompositionKey = useRef<string | null>(null);

  useEffect(() => {
    if (count < 2 || reportedCompositionKey.current === compositionKey) {
      return;
    }

    reportedCompositionKey.current = compositionKey;

    const report: CarouselReport = {
      location: 'homepage',
      widgetCount: count,
      widgets: deck.map((widget, index) => ({ ...widget.trackingMetadata, position: index + 1 })),
    };

    trackEnterpriseEvent('widget::carousel::shown', report);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on `compositionKey`, which stands in for `deck` and `count`; the array identity churns every render
  }, [compositionKey, trackEnterpriseEvent]);

  if (count === 0) {
    return null;
  }

  // A single card is not a carousel: no slot, no carousel id, and no `widget::carousel::shown`.
  if (count === 1) {
    return <MarketingWidgetTemplate {...deck[0]} />;
  }

  const frontIndex = activeIndex % count;
  const advance = (direction: 'next' | 'previous') => {
    const toIndex = direction === 'next' ? (frontIndex + 1) % count : (frontIndex - 1 + count) % count;
    setActiveIndex(toIndex);
    // Short-term RUM only. Advancing is not a widget::clicked track event.
    addAction('enterprise::marketing-widget::carousel::advanced', {
      direction,
      fromIndex: frontIndex,
      toIndex,
      widgetCount: count,
      fromWidget: getWidgetKey(deck[frontIndex], frontIndex),
      toWidget: getWidgetKey(deck[toIndex], toIndex),
    });
  };

  return (
    <div className="flex h-full flex-col gap-3" data-testid="marketing-widget-deck">
      <div className="grid flex-1" style={{ paddingTop: (count - 1) * PEEK_OFFSET_PX }}>
        {deck.map((widget, index) => {
          // Rotating offset from the front card, for rendering only — 0 is whatever is in front right
          // now. The reported slot is `index + 1`, which does not move when the supplier advances.
          const position = (index - frontIndex + count) % count;
          const isFront = position === 0;

          return (
            <div
              key={getWidgetKey(widget, index)}
              data-testid="marketing-widget-deck-card"
              data-position={position}
              aria-hidden={!isFront}
              inert={!isFront}
              className={cn(
                'relative col-start-1 row-start-1 origin-top rounded-2xl bg-white',
                'transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none'
              )}
              style={{
                zIndex: count - position,
                transform: `translateY(${-position * PEEK_OFFSET_PX}px) scale(${1 - position * PEEK_SCALE_STEP})`,
              }}
            >
              <MarketingWidgetTemplate {...widget} isFront={isFront} position={index + 1} />
              <div
                aria-hidden="true"
                className={cn(
                  'pointer-events-none absolute inset-0 rounded-2xl bg-canvas',
                  'transition-opacity duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none'
                )}
                style={{ opacity: position * PEEK_FOG_STEP }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between">
        <Button variant="ancillary" size="sm" onClick={() => advance('previous')}>
          {t('core.previousPage')}
        </Button>
        <span className="sr-only" aria-live="polite">
          {t('core.countOfTotal', { count: frontIndex + 1, total: count })}
        </span>
        <div aria-hidden="true" className="flex items-center gap-[5px]" data-testid="marketing-widget-deck-dots">
          {deck.map((widget, index) => (
            <span
              key={getWidgetKey(widget, index)}
              data-testid="marketing-widget-deck-dot"
              data-active={index === frontIndex}
              className={cn(
                'size-2 shrink-0 rounded-full bg-text-secondary',
                // text-secondary is #211F1F at 64%, the active dot. 25% of that is the 16% inactive dot.
                { 'opacity-25': index !== frontIndex }
              )}
            />
          ))}
        </div>
        <Button variant="ancillary" size="sm" onClick={() => advance('next')}>
          {t('core.nextPage')}
        </Button>
      </div>
    </div>
  );
};

export default MarketingWidgetDeck;
